import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BookOpen, Users } from 'lucide-react'
import { ProgrammeForm, ProgrammeFormValues, mapProgrammeFormValuesToPayload } from '@/components/app/programmes/ProgrammeForm'
import { programmesService } from '@/services/programmesService'
import { departmentsService } from '@/services/departmentsService'
import { ProgrammeModel } from '@/models/programme.model'
import { Department } from '@/models/department.model'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { paths } from '@/routes/path'
import BackButton from '@/components/ui/BackButton'

export const ProgrammeEditPage: React.FC = () => {
  const { programmeId } = useParams<{ programmeId: string }>()
  const navigate = useNavigate()
  const [programme, setProgramme] = useState<ProgrammeModel | null>(null)
  const [departments, setDepartments] = useState<Department[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadData = async () => {
      if (!programmeId) {
        setError('Programme ID is missing.')
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const [departmentsResponse, programmesResponse] = await Promise.all([
          departmentsService.getDepartmentsPage({ page: 1, perPage: 100 }),
          programmesService.getAllProgrammes({ perPage: 100, requireAuth: true }),
        ])

        setDepartments(departmentsResponse.data)
        const matched = programmesResponse.data.find((item) => item.id === programmeId)
        if (!matched) {
          setError('Programme not found.')
        }
        setProgramme(matched ?? null)
      } catch (err) {
        setError((err as Error).message || 'Unable to load programme details.')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [programmeId])

  const handleSubmit = async (values: ProgrammeFormValues) => {
    if (!programmeId) return

    setSubmitting(true)
    setError(null)
    try {
      const payload = mapProgrammeFormValuesToPayload(values)
      await programmesService.updateProgramme(programmeId, payload)
      navigate(paths.app.programmes.root.absolute)
    } catch (err) {
      setError((err as Error).message || 'Unable to update programme.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return <div className="flex min-h-[50vh] items-center justify-center text-secondary-500">Loading programme…</div>
  }

  if (error) {
    return (
      <Card className="mx-auto max-w-2xl">
        <CardContent className="space-y-4 p-6 text-center">
          <h1 className="text-2xl font-semibold text-secondary-900">Unable to edit programme</h1>
          <p className="text-secondary-600">{error}</p>
          <Button onClick={() => navigate(paths.app.programmes.root.absolute)}>Back to Programmes</Button>
        </CardContent>
      </Card>
    )
  }

  if (!programme) {
    return (
      <Card className="mx-auto max-w-2xl">
        <CardContent className="space-y-4 p-6 text-center">
          <h1 className="text-2xl font-semibold text-secondary-900">Programme not found</h1>
          <Button onClick={() => navigate(paths.app.programmes.root.absolute)}>Back to Programmes</Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <div className="flex items-center space-x-2">
          <BackButton/>
          <h1 className="text-3xl font-bold text-secondary-900">{programme?.title??'Edit Programme'}</h1>
        </div>
        <p className="text-secondary-600">Update programme information, payment accounts, and admission requirements.</p>
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => navigate(paths.app.programmes.cohorts.build(programme.id ?? ''))}
          >
            <Users className="mr-2 h-4 w-4" />
            Manage Cohorts
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(paths.app.programmes.curriculum.build(programme.id ?? ''))}
          >
            <BookOpen className="mr-2 h-4 w-4" />
            Manage Curriculum
          </Button>
        </div>
      </header>

      <Card className='p-5'>
        <ProgrammeForm
          mode="edit"
          programme={programme}
          departments={departments.map((department) => ({ id: department.id, name: department.name }))}
          submitting={submitting}
          onSubmit={handleSubmit}
          onCancel={() => navigate(paths.app.programmes.root.absolute)}
          submitLabel="Save Changes"
        />
      </Card>
    </div>
  )
}

export default ProgrammeEditPage
